import React, { useEffect, useRef, useState } from "react";

import useAppInsightsHandler from "hooks/useAppInsightsHandler.js";

import { getInsightList, updateAllInsight, updateInsight } from "api/activeInsight";

import { getRowFormattedData } from "utils/utils";
import { AlarmPageStrings, INSIGHT_TABLE_STRINGS, InsightsStrings } from "constants/constantStrings";
import { CHECKBOX_STATES } from "constants/globalConstants";

import AgTable from "components/AgTable";
import Filter from "components/Filter";
import PageLoading from "components/PageLoading";
import TableDialog from "components/TableDialog";

import InsightsSummary from "../InsightsSummary";

import { getInsightListConfig } from "./InsightList.table.config";

const InsightsList = ({ setInsightsListCount }) => {
  const [anomalyList, setAnomalyList] = useState([]);
  const [filterValue, setFilterValue] = useState("");
  const [showTableFilterIcon, setShowTableFilterIcon] = useState(false);
  const gridRef = useRef();
  const [isLoading, setIsLoading] = useState(true);
  const [popupData, setPopupData] = useState();
  const [columdefs, setColDefs] = useState(null);
  const objAppInsights = useAppInsightsHandler();

  useEffect(() => {
    async function fetchData() {
      const response = await getInsightList();
      if (response && response.data) {
        setAnomalyList(response.data);
        updateColDef(response.data);
        setInsightsListCount(response.data.length);
      }
      setIsLoading(false);
    }
    fetchData();
  }, []);

  useEffect(() => {
    updateColDef(getTableData());
  }, [showTableFilterIcon]);

  const updateColDef = anomalyList => {
    const Alert = geColHeaderCheckboxState(anomalyList, "Alert");
    const ShowInReport = geColHeaderCheckboxState(anomalyList, "ShowInReport");
    const defs = getInsightListConfig(showTableFilterIcon, { Alert, ShowInReport });
    setColDefs(defs);
  };

  const geColHeaderCheckboxState = (sourceList, field) => {
    let state = CHECKBOX_STATES.INDETERMINATE;
    const checkedAlarms = sourceList.filter(alarm => Boolean(alarm[field]));
    if (checkedAlarms.length === 0) {
      state = CHECKBOX_STATES.UNCHECKED;
    } else if (checkedAlarms.length === sourceList.length) {
      state = CHECKBOX_STATES.CHECKED;
    } else {
      state = CHECKBOX_STATES.INDETERMINATE;
    }
    return state;
  };

  const getGridAPI = () => gridRef?.current?.api;

  const onColumnSelection = (colKey, value) => {
    const gridAPI = getGridAPI();
    if (gridAPI) {
      const itemsToUpdate = [];
      gridAPI.forEachNodeAfterFilterAndSort(node => {
        const { data } = node;
        data[colKey] = value;
        itemsToUpdate.push(data);
      });
      gridAPI.applyTransaction({ update: itemsToUpdate });
      setAnomalyList(itemsToUpdate);
    }
    updateAllInsight({ [colKey]: value });
  };

  const getTableData = () => {
    const updatedTags = [];
    const gridAPI = getGridAPI();
    if (gridAPI) {
      gridAPI.forEachNode(node => updatedTags.push(node.data));
    }
    return updatedTags;
  };

  const onInsightsUpdate = data => {
    objAppInsights.trackEvent("insightsListRowClick");
    updateInsight(data);
    const itemsToUpdate = getTableData();
    updateColDef(itemsToUpdate);
  };

  const onFilterChange = val => {
    objAppInsights.trackEvent("insightsListFilterChange");
    setFilterValue(val);
  };

  const closePopup = () => setPopupData(null);
  const onRowClicked = ({ data }) => {
    objAppInsights.trackEvent("insightsListRowClick");
    const rowData = getRowFormattedData(columdefs, data);
    const title = data.AnomalyType === InsightsStrings.FIT1 ? InsightsStrings.feedFlow : InsightsStrings.brineFlow;
    const subTitle = INSIGHT_TABLE_STRINGS.viewInsight;
    setPopupData({ title, subTitle, rowData });
  };

  return (
    <>
      {isLoading ? (
        <PageLoading customHeight={"calc(100vh - 150px)"} />
      ) : (
        <>
          <div className="d-flex">
            <div className="margin-right-30">
              <InsightsSummary descriptionText={InsightsStrings.insightListDesc} />
            </div>
            <div className="jc-end">
              <Filter
                data={anomalyList}
                placeholder={AlarmPageStrings.searchHere}
                setFilterValue={onFilterChange}
                setShowTableFilterIcon={setShowTableFilterIcon}
                showTableFilterIcon={showTableFilterIcon}
                showfilter={false}
                customClass="marginBtm10"
              />
            </div>
          </div>
          <>
            {anomalyList && (
              <div className="marginTop20">
                <AgTable
                  gridRef={gridRef}
                  rowData={anomalyList}
                  onRowClicked={onRowClicked}
                  columnDefs={columdefs}
                  customHeight="calc(100vh - 215px)"
                  context={{ onInsightsUpdate, onColumnSelection }}
                  quickFilterText={filterValue}
                  setShowTableFilterIcon={setShowTableFilterIcon}
                  showTableFilterIcon={showTableFilterIcon}
                  setBadgeCount={setInsightsListCount}
                />
              </div>
            )}
          </>
        </>
      )}
      {popupData && <TableDialog popupData={popupData} closePopup={closePopup} />}
    </>
  );
};

export default InsightsList;
